import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { useAuth } from "../context/AuthContext";
import {
	AlertTriangle,
	CheckCircle,
	Info,
	ThumbsUp,
	ArrowUpCircle,
} from "lucide-react";
import {
	RadarChart,
	PolarGrid,
	PolarAngleAxis,
	PolarRadiusAxis,
	Radar,
	ResponsiveContainer,
} from "recharts";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const PromptAnalyzer = () => {
	const { user } = useAuth();
	const [prompt, setPrompt] = useState("");
	const [analysis, setAnalysis] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [improvedPrompt, setImprovedPrompt] = useState(null);
	const [isImprovementLoading, setIsImprovementLoading] = useState(false);
	const [improvementError, setImprovementError] = useState(null);

	// Load data from localStorage on component mount
	useEffect(() => {
		const savedPrompt = localStorage.getItem("userPrompt");
		const savedAnalysis = localStorage.getItem("promptAnalysis");
		const savedImprovedPrompt = localStorage.getItem("improvedPrompt");

		if (savedPrompt) {
			setPrompt(savedPrompt);
		}

		if (savedAnalysis) {
			try {
				setAnalysis(JSON.parse(savedAnalysis));
			} catch (err) {
				console.error(
					"Erreur lors du chargement de l'analyse sauvegardée:",
					err
				);
			}
		}

		if (savedImprovedPrompt) {
			try {
				setImprovedPrompt(JSON.parse(savedImprovedPrompt));
			} catch (err) {
				console.error(
					"Erreur lors du chargement du prompt amélioré sauvegardé:",
					err
				);
			}
		}
	}, []);

	// Save data to localStorage when they change
	useEffect(() => {
		if (prompt) {
			localStorage.setItem("userPrompt", prompt);
		}

		if (analysis) {
			localStorage.setItem("promptAnalysis", JSON.stringify(analysis));
		}

		if (improvedPrompt) {
			localStorage.setItem("improvedPrompt", JSON.stringify(improvedPrompt));
		}
	}, [prompt, analysis, improvedPrompt]);

	const analyzePrompt = async () => {
		if (!prompt.trim()) return;

		setIsLoading(true);
		setError(null);
		setImprovedPrompt(null); // Reset improved prompt when analyzing a new prompt

		try {
			const headers = {
				"Content-Type": "application/json",
			};
			
			// Add auth token if user is logged in
			if (user && user.token) {
				headers["Authorization"] = `Bearer ${user.token}`;
			}

			const response = await fetch("/api/analyze", {
				method: "POST",
				headers,
				body: JSON.stringify({ prompt }),
			});

			if (!response.ok) {
				if (response.status === 429) {
					throw new Error("Limite de taux atteinte. Connectez-vous pour obtenir plus de requêtes.");
				}
				throw new Error(`Erreur API: ${response.status}`);
			}

			const data = await response.json();
			setAnalysis(data);
		} catch (err) {
			console.error("Erreur lors de l'analyse du prompt:", err);
			setError("Échec de l'analyse du prompt. Veuillez réessayer.");
		} finally {
			setIsLoading(false);
		}
	};

	const improvePrompt = async () => {
		if (!analysis) return;

		setIsImprovementLoading(true);
		setImprovementError(null);

		// Debug logging
		console.log("Starting prompt improvement request with data:", {
			original_prompt: prompt,
			strengths: analysis.strengths,
			improvements: analysis.improvements,
			suggestions: analysis.suggestions,
		});

		try {
			// Debug - log the request URL
			console.log("Sending request to:", "http://localhost:8000/improve");

			const headers = {
				"Content-Type": "application/json",
			};
			
			// Add auth token if user is logged in
			if (user && user.token) {
				headers["Authorization"] = `Bearer ${user.token}`;
			}

			const response = await fetch("/api/improve", {
				method: "POST",
				headers,
				body: JSON.stringify({
					original_prompt: prompt,
					strengths: analysis.strengths,
					improvements: analysis.improvements,
					suggestions: analysis.suggestions,
				}),
			});

			// Debug - log the response status
			console.log("Response status:", response.status);
			console.log(
				"Response headers:",
				Object.fromEntries([...response.headers])
			);

			if (!response.ok) {
				if (response.status === 429) {
					throw new Error("Limite de taux atteinte. Connectez-vous pour obtenir plus de requêtes.");
				}
				throw new Error(`Erreur API: ${response.status}`);
			}

			const data = await response.json();
			console.log("Received data:", data);
			setImprovedPrompt(data);
		} catch (err) {
			console.error("Erreur lors de l'amélioration du prompt:", err);
			// Log more details about the error
			console.error("Error details:", {
				name: err.name,
				message: err.message,
				stack: err.stack,
			});
			setImprovementError(
				"Échec de l'amélioration du prompt. Veuillez réessayer."
			);
		} finally {
			setIsImprovementLoading(false);
		}
	};

	const getScoreColor = (score) => {
		if (score >= 80) return "text-green-500";
		if (score >= 60) return "text-yellow-500";
		return "text-red-500";
	};

	// Traduire les noms des dimensions dans le graphique radar
	const translateDimension = (key) => {
		const translations = {
			clarity: "Clarté",
			specificity: "Spécificité",
			context: "Contexte",
			structure: "Structure",
			actionability: "Actionabilité",
		};
		return translations[key] || key.charAt(0).toUpperCase() + key.slice(1);
	};

	const chartData = analysis?.scores
		? Object.entries(analysis.scores).map(([key, value]) => ({
				subject: translateDimension(key),
				score: value,
		  }))
		: [];

	// Handle copying improved prompt to clipboard
	const copyImprovedPrompt = () => {
		if (!improvedPrompt?.improved_prompt) return;

		navigator.clipboard
			.writeText(improvedPrompt.improved_prompt)
			.then(() => {
				alert("Prompt amélioré copié dans le presse-papiers !");
			})
			.catch((err) => {
				console.error("Erreur lors de la copie:", err);
				alert("Erreur lors de la copie du prompt.");
			});
	};

	// Handle using improved prompt for new analysis
	const useImprovedPrompt = () => {
		if (!improvedPrompt?.improved_prompt) return;

		setPrompt(improvedPrompt.improved_prompt);
		setAnalysis(null);
		setImprovedPrompt(null);
	};

	return (
		<div className="w-full max-w-6xl mx-auto p-6 space-y-6">
			<Card>
				<CardHeader>
					<CardTitle className="text-2xl font-bold">
						Analyseur de Prompts
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<Textarea
							placeholder="Collez votre prompt ici..."
							className="min-h-32"
							value={prompt}
							onChange={(e) => setPrompt(e.target.value)}
							disabled={isLoading}
						/>
						{error && (
							<div className="text-red-500 text-sm flex items-center gap-2">
								<AlertTriangle size={16} />
								<span>{error}</span>
							</div>
						)}
						<Button
							onClick={analyzePrompt}
							className="w-full"
							disabled={!prompt.trim() || isLoading}
						>
							{isLoading ? "Analyse en cours..." : "Analyser le Prompt"}
						</Button>
					</div>
				</CardContent>
			</Card>

			{analysis && (
				<>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<Card>
							<CardHeader>
								<CardTitle>Graphique d'Évaluation</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="h-64">
									<ResponsiveContainer width="100%" height="100%">
										<RadarChart data={chartData}>
											<PolarGrid />
											<PolarAngleAxis dataKey="subject" />
											<PolarRadiusAxis angle={30} domain={[0, 100]} />
											<Radar
												name="Score"
												dataKey="score"
												stroke="#2563eb"
												fill="#2563eb"
												fillOpacity={0.6}
											/>
										</RadarChart>
									</ResponsiveContainer>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Analyse Détaillée</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="space-y-4">
									<div className="flex items-center justify-between">
										<span className="text-lg font-medium">Score Global</span>
										<span
											className={`text-2xl font-bold ${getScoreColor(
												analysis.overallScore
											)}`}
										>
											{analysis.overallScore}/100
										</span>
									</div>

									<div className="space-y-2">
										<div className="flex items-start gap-2">
											<CheckCircle className="text-green-500 mt-1" size={20} />
											<div>
												<h4 className="font-medium">
													Points Forts ({analysis.strengthCount})
												</h4>
												<ul className="list-disc list-inside text-sm text-gray-600">
													{analysis.strengths &&
													analysis.strengths.length > 0 ? (
														analysis.strengths.map((strength, index) => (
															<li key={index}>{strength}</li>
														))
													) : (
														<li>Aucun point fort identifié</li>
													)}
												</ul>
											</div>
										</div>

										<div className="flex items-start gap-2">
											<AlertTriangle className="text-red-500 mt-1" size={20} />
											<div>
												<h4 className="font-medium">
													Points à Améliorer ({analysis.weaknessCount})
												</h4>
												<ul className="list-disc list-inside text-sm text-gray-600">
													{analysis.improvements &&
													analysis.improvements.length > 0 ? (
														analysis.improvements.map((improvement, index) => (
															<li key={index}>{improvement}</li>
														))
													) : (
														<li>Aucun point à améliorer identifié</li>
													)}
												</ul>
											</div>
										</div>
									</div>

									{!improvedPrompt && (
										<Button
											onClick={improvePrompt}
											className="w-full mt-4 bg-green-600 hover:bg-green-700"
											disabled={isImprovementLoading}
										>
											{isImprovementLoading ? (
												"Amélioration en cours..."
											) : (
												<>
													<ArrowUpCircle className="mr-2" size={16} />
													Améliorer ce Prompt
												</>
											)}
										</Button>
									)}

									{improvementError && (
										<div className="text-red-500 text-sm flex items-center gap-2 mt-2">
											<AlertTriangle size={16} />
											<span>{improvementError}</span>
										</div>
									)}
								</div>
							</CardContent>
						</Card>
					</div>

					{improvedPrompt && (
						<Card className="border-green-500 border-2">
							<CardHeader className="bg-green-50">
								<CardTitle className="text-green-700 flex items-center">
									<ThumbsUp className="mr-2" size={20} />
									Prompt Amélioré
								</CardTitle>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="p-4 bg-gray-50 rounded-md border border-gray-200">
									<h4 className="font-medium mb-2 text-gray-700">
										Version Améliorée:
									</h4>
									<div className="whitespace-pre-wrap text-gray-800 font-medium">
										{improvedPrompt.improved_prompt}
									</div>
								</div>

							<div className="p-4 bg-blue-50 rounded-md border border-blue-200">
								<h4 className="font-medium mb-2 text-blue-700 flex items-center">
									<Info className="mr-2" size={16} />
									Explication des Améliorations:
								</h4>
								<div className="text-blue-800 text-sm prose prose-sm max-w-none prose-headings:text-blue-900 prose-p:text-blue-800 prose-strong:text-blue-900 prose-ul:text-blue-800 prose-ol:text-blue-800">
									<ReactMarkdown remarkPlugins={[remarkGfm]}>
										{improvedPrompt.explanation}
									</ReactMarkdown>
								</div>
							</div>

								<div className="flex flex-col sm:flex-row gap-3">
									<Button
										onClick={copyImprovedPrompt}
										variant="outline"
										className="flex-1"
									>
										Copier le Prompt Amélioré
									</Button>
									<Button
										onClick={useImprovedPrompt}
										className="flex-1 bg-green-600 hover:bg-green-700"
									>
										Analyser le Prompt Amélioré
									</Button>
								</div>
							</CardContent>
						</Card>
					)}
				</>
			)}
		</div>
	);
};

export default PromptAnalyzer;
